import { collection, deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import type {
  AreaReportSnapshot,
  AreaReportSnapshotAxisCount,
  EvaluationPolarityCounts,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

export interface SaveAreaReportSnapshotInput {
  districtId: string;
  name: string;
  polygon: { latitude: number; longitude: number }[];
  postIds: string[];
  pointCount: number;
  axisCounts: AreaReportSnapshotAxisCount[];
  summary: string | null;
  /** 集計に使った調査項目の key (docs/27 §9)。 */
  axisKey?: string | null;
  /** 評価の内訳 (評価軸を持つテンプレートのみ)。 */
  evaluation?: EvaluationPolarityCounts | null;
  /** 集計した調査 (docs/98 R-97、未指定 = 既定の調査)。 */
  surveyKey?: string | null;
}

/**
 * エリア選択集計を名前付きスナップショットとして保存する (`deleteStreetSurveyPost` と同じ
 * 「client SDK が直接書込み、firestore.rules の isDistrictAdmin に委任」方式。追加のサーバー
 * 側計算は不要 (AI要約は事前に `generateAreaSummary` callable で生成済みの値をそのまま渡す)。
 */
export async function saveAreaReportSnapshot(
  input: SaveAreaReportSnapshotInput,
  uid: string,
): Promise<string> {
  const ref = doc(collection(db, 'districts', input.districtId, 'areaReports'));
  const data: Omit<AreaReportSnapshot, 'createdAt'> & {
    createdAt: ReturnType<typeof serverTimestamp>;
  } = {
    districtId: input.districtId,
    name: input.name,
    polygon: input.polygon,
    postIds: input.postIds,
    pointCount: input.pointCount,
    axisCounts: input.axisCounts,
    axisKey: input.axisKey ?? null,
    evaluation: input.evaluation ?? null,
    surveyKey: input.surveyKey && input.surveyKey !== 'default' ? input.surveyKey : null,
    summary: input.summary,
    createdAt: serverTimestamp(),
    createdBy: uid,
  };
  await setDoc(ref, data);
  return ref.id;
}

export async function deleteAreaReportSnapshot(
  districtId: string,
  reportId: string,
): Promise<void> {
  await deleteDoc(doc(db, 'districts', districtId, 'areaReports', reportId));
}
